import { EventEmitter, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpHeaders } from '@angular/common/http';
import { nullOptional, sortIndexAscend, ObjectUtils, Sorter } from '@ark-libs/ngx-utils';

import { DataDictService } from '../../services/service/data-dict.service';
import { UserContextService } from '../../services/service/user-context.service';


export enum ListViewRowSelectMode {
    SINGLE = 'single', // 单选
    MULTI = 'nulti',   // 多选
    NONE = 'none'      // 不可选
}
export enum ListViewSortMode {
    SINGLE = 'single', // 仅支持单个字段排序
    MULTI = 'multi'    // 支持多个字段排序
}
export enum ListViewSortDirect {
    DESCEND = 'descend', // 倒序
    ASCEND = 'ascend'    // 正序
}
export enum ListViewCheckedSate {
    ALL_CHECKED = 'all-checked',     // 全部选中
    ALL_UNCHECKED = 'all-unchecked', // 全部不选中
    INDETERMINATE = 'indeterminate'  // 部分选中部分未选中
}
export enum ListViewColType {
    FIELD = 'field',           // 字段
    CHECKBOX = 'checkbox',     // 选择列
    OPERATIONS = 'operations', // 行号
}

export interface ListViewOptions {
    enableStateCache?: boolean,
    rowSelectMode?: ListViewRowSelectMode | string,
    rowSelectFieldName?: string,
    showOperations?: boolean,
    headNowrap?: boolean,
    contentNowrap?: boolean,
    pageSize?: number,
    sortMode?: ListViewSortMode | string,
    params?: { [name: string]: any },
    fields?: ListViewFieldOptions[] | { [name: string]: ListViewFieldOptions | any } | any[],
    pagination?: ListViewPaginationOptions | any,
    data?: any
}
export interface ListViewFieldOptions {
    name?: string;
    label?: string;
    hidden?: boolean;
    maxWidth?: string;
    headNowrap?: boolean;
    headStyle?: any;
    headAlign: 'center' | 'right' | 'left' | string;
    headTemplateEnabled?: boolean;
    dataTemplateEnabled?: boolean;
    formater?: Function;
    index?: number;
    dictionary?: any;
    dictionaryRef?: string;
    contentNowrap?: boolean;
    contentStyle?: any;
    contentAlign: 'center' | 'right' | 'left' | string;
    sort?: ListViewSortOptions | any;
    filter?: ListViewFilterOptions | any;
}
export interface ListViewPaginationOptions {
    pageSize?: number;
    pageSizeSelectorValues?: number[];
    showSizeChanger?: boolean;
    showQuickJumper?: boolean;
    showTotal?: boolean;
    hidden?: boolean;
}
export interface ListViewSortOptions {
    enabled?: boolean;
    value?: ListViewSortDirect | string;
    sortIndex?: number;
}
export interface ListViewFilterOptions {
    items?: ListViewFilterItemOptions[] | any[];
    enabled?: boolean;
}
export interface ListViewFilterItemOptions {
    text?: string;
    value?: any;
    seq?: number;
    selected?: boolean;
}


// {
// 	name: {
// 		name: "name",
// 		label: "姓名",
// 		sort: { enabled: true,  value: null,  },
// 		query: {},
// 		filter: { enabled: true, value:[], items:[{ text: '张三', value: 'Joe', seq: 2, selected:false }, { text: '李四', value: 'Jim', seq: 1 }] }
// 	},
// };
export class ListView {
    private _injector: Injector;
    allSelected: boolean;
    indeterminateSelected: boolean;

    rowSelectMode: string = ListViewRowSelectMode.NONE;
    rowSelectFieldName: string = '__selected';
    showOperations: boolean = false;
    showLoading: boolean = false;
    sortMode: string = ListViewSortMode.SINGLE;
    headNowrap: boolean = true;
    contentNowrap: boolean = true;

    fields: { [name: string]: ListViewField };
    private _indexedFields: ListViewField[] = [];
    pagination: ListViewPagination;
    private _data: any[];

    private _stateKey;
    private _initFromState: boolean = false;
    enableStateCache: boolean = true; // 启用状态缓存，返回页面时状态自动还原
    stateCacheKey: string;

    private _searchValue: string;
    private _defaultSortValue = null;

    params: any = {};  // 用于记录页面的部分参数，特别是查询条件参数（配合实现listview的记忆功能）

    sortChange: EventEmitter<any> = new EventEmitter();
    filterChange: EventEmitter<any> = new EventEmitter();
    queryChange: EventEmitter<any> = new EventEmitter();
    paginationChange: EventEmitter<any> = new EventEmitter();
    injectorAssigned: EventEmitter<any> = new EventEmitter();

    constructor(options?: ListViewOptions | any, injector?: Injector) {
        this.patchOptions(options);
        this.injector = injector;
        this._defaultSortValue = this.sortValue;
        this.fromState();
    }

    set injector(v: Injector) {
        if (this._injector == v) return;
        this._injector = v;
        this.injectorAssigned.emit(v);
    }
    get injector(): Injector {
        return this._injector;
    }

    private patchOptions(options?: any) {
        if (!options) { options = {}; }

        ObjectUtils.patchDataWhileNotNull(options, this, true);
        ObjectUtils.patchDataWhileNotNull(options.params, this.params, false);

        this.fields = {};
        this.pagination = new ListViewPagination(this, options.pagination);
        if (options.fields) {
            let defaultFieldIndex = 100000;
            const parseFields = (item) => {
                if (item == null) { return; }
                if (item instanceof ListViewField) {
                    item.listView = this;
                    this.fields[item.name] = item;
                } else {
                    this.fields[item.name] = new ListViewField(this, item);
                }
                this.fields[item.name].index = this.fields[item.name].index || (defaultFieldIndex++);
            };
            if (Array.isArray(options.fields)) {
                options.fields.forEach(item => parseFields(item));
            } else {
                Object.keys(options.fields).forEach(key => {
                    options.fields[key].name = key;
                    parseFields(options.fields[key]);
                });
            }
            this._indexedFields = Object.values(this.fields).sort(Sorter.sortIndexAscend);
        }

        if (options.data != null) this.data = options.data;
        else if (this._data == null) this._data = [];
        // console.log(this);
    }
    get pageSize(): number {
        return this.pagination ? this.pagination.pageSize : 10;
    }
    set pageSize(v: number) {
        if (this.pagination) this.pagination.pageSize = v;
    }
    get pageIndex(): number {
        return this.pagination ? this.pagination.pageIndex : 1;
    }
    set pageIndex(v: number) {
        if (this.pagination) this.pagination.pageIndex = v;
    }
    get total(): number {
        return this.pagination ? this.pagination.total : 0;
    }
    set total(v: number) {
        if (this.pagination) this.pagination.total = v;
    }
    setTotal(headers: HttpHeaders | { [key: string]: any }) {
        const total = headers instanceof HttpHeaders ? headers.get("X-Total-Count") : headers["X-Total-Count"];
        if (total != null) this.pagination.total = Number.parseInt(total);
    }

    get data(): any[] {
        return this._data;
    }
    set data(v: any[]) {
        this._data = v;
        this.showLoading = false;
        this.saveState();
    }

    // ------------------------------------------------------
    // fields
    // ------------------------------------------------------
    get indexedFields(): ListViewField[] {
        return this._indexedFields;//[...Object.values(this.fields).sort(sortIndexAscend)];
    }

    // ------------------------------------------------------
    // filters
    // ------------------------------------------------------
    get filterValue(): { [name: string]: any[] } {
        const result = {};
        Object.values(this.fields)
            .filter(field => field.filter && field.filter.enabled && field.filter.value != null && field.filter.value.length > 0)
            .forEach(field => { result[field.name] = field.filter.value; });
        return result;
    }
    get filterValueToQueryStatement(): string {
        // TODO: 将过滤条件转换为服务器可识别的SimpleStatement结构字串
        // code here
        //
        return null;
    }
    clearFilterValue() {
        Object.values(this.fields)
            .filter(field => field.filter != null)
            .map(field => field.filter)
            .forEach(filter => { filter.value = []; });
    }

    // ------------------------------------------------------
    // sorts
    // ------------------------------------------------------
    get sortValue(): { [name: string]: ListViewSortDirect | string } {
        const result = {};
        const fields = [...Object.values(this.fields).filter(field => field.sort && field.sort.enabled && field.sort.value != null)];
        fields.sort((a, b) => a.sort.sortIndex > b.sort.sortIndex ? 1 : -1).forEach(field => { result[field.name] = field.sort.value; });
        return result;
    }
    set sortValue(value: { [name: string]: ListViewSortDirect | string }) {
        if (!value) { value = {}; }
        //const fieldName = Object.keys(value).shift();
        const sortDirect = Object.values(value).shift();

        Object.values(this.fields).filter(field => field.sort != null)
            .forEach(field => {
                if (field.name === value.name) {
                    field.sort.value = sortDirect;
                    field.sort.sortIndex = 1;
                } else {
                    field.sort.value = null;
                    field.sort.sortIndex = null;
                }
            });
    }
    get sortValueToQueryStatement(): string[] {
        let sortValues = this.sortValue;
        if (Object.values(sortValues).length == 0) sortValues = this._defaultSortValue;
        return Object.keys(sortValues)
            .map(key => `${key},${sortValues[key] === ListViewSortDirect.ASCEND ? 'asc' : (sortValues[key] === ListViewSortDirect.DESCEND ? 'desc' : sortValues[key])}`);
    }
    clearSortValue() {
        Object.values(this.fields).filter(field => field.sort != null)
            .forEach(field => { field.sort.value = null; field.sort.sortIndex = null; });
    }

    // ------------------------------------------------------
    // selections
    // ------------------------------------------------------
    toggleAllSelected(checked: boolean) {
        this.indeterminateSelected = false;
        const fieldName = this.rowSelectFieldName;
        if (checked) {
            this.data.forEach(row => { row[fieldName] = true; });
        } else {
            this.data.forEach(row => { row[fieldName] = false; });
        }
    }
    rowSelectedChange(checkRow) {
        const fieldName = this.rowSelectFieldName;
        if (this.rowSelectMode === 'multi' || this.rowSelectMode === 'nulti') {
            const unselected = this.data.every(row => !row[fieldName]);
            const selected = this.data.every(row => row[fieldName] === true);
            this.allSelected = selected;
            this.indeterminateSelected = (!selected) && (!unselected);
        } else {
            this.data.forEach(row => {
                if (row !== checkRow) { row[fieldName] = false; }
            });
        }
    }
    get selectedRows(): any[] {
        return this.data.filter(row => row[this.rowSelectFieldName] === true);
    }

    // ------------------------------------------------------
    // state
    // ------------------------------------------------------
    private parseFullStateKey() {
        const url = this.injector ? this.injector.get(Router).url : null;
        return "listview-state:" + (this.stateCacheKey || '') + "[" + (url || '') + "][" + Object.keys(this.fields).join(",") + "]";
    }
    private saveState() {
        if (!this.enableStateCache || !this.injector) return;
        this.injector.get(UserContextService).putData(this.parseFullStateKey(), this);
    }
    private fromState() {
        if (!this.enableStateCache || !this.injector) return;
        const state = this.injector.get(UserContextService).getData(this.parseFullStateKey());
        if (!state) return;

        this.pagination.pageIndex = state.pagination.pageIndex;
        this.pagination.pageSize = state.pagination.pageSize;
        let field: ListViewField;
        Object.keys(this.fields).forEach(key => {
            field = this.fields[key];
            field.filter.value = state.fields[key].filter.value;
            //field.query.value = state.fields[key].query.value;
            field.sort.value = state.fields[key].sort.value;
            field.sort.sortIndex = state.fields[key].sort.sortIndex;
        });
        if (state.params) {
            Object.keys(state.params).forEach(key => {
                this.params[key] = state.params[key];
            });
        }
    }

    // ------------------------------------------------------
    // search
    // ------------------------------------------------------

    public get searchValue(): string {
        return this._searchValue;
    }
    private _searchValueChangeTimer;
    public set searchValue(v: string) {
        this._searchValue = v;
        if (this._searchValueChangeTimer) clearTimeout(this._searchValueChangeTimer);
        this._searchValueChangeTimer = setTimeout(() => {
            this._searchValueChangeTimer = null;
            this.queryChange.emit();
        }, 500);
    }
}

export class ListViewPagination {
    listView: ListView;

    _pageIndex: number = 1;
    _pageSize: number = 10;
    _total: number = 0;
    pageSizeSelectorValues: number[] = [10, 20, 30, 40, 50, 100, 200];
    showSizeChanger: boolean = true;
    showQuickJumper: boolean = true;
    showTotal: boolean = true;
    hidden: boolean = false;

    constructor(listView: ListView, options?: ListViewPaginationOptions | any) {
        this.listView = listView;
        if (options && options.pageSizeSelectorValues) this.pageSizeSelectorValues = options.pageSizeSelectorValues.join(',').split(',');
        ObjectUtils.patchDataWhileNotNull(options, this, true);
        if (this.pageSizeSelectorValues.indexOf(this.pageSize) == -1) {
            this.pageSizeSelectorValues.unshift(this.pageSize);
        }
    }

    public get total(): number {
        return this._total;
    }

    public set total(v: number) {
        if (this._total == v) return;
        this._total = v;
        this.pageIndex = Math.min(Math.ceil(v / this._pageSize), this._pageIndex);
    }

    public get pageIndex(): number {
        return this._pageIndex;
    }

    public set pageIndex(v: number) {
        if (this._pageIndex == v || v <= 0) return;
        this._pageIndex = v;
        this.listView.paginationChange.emit();
    }

    public get pageSize(): number {
        return this._pageSize;
    }

    public set pageSize(v: number) {
        if (this._pageSize == v || v < 0) return;
        this._pageSize = v;
        this.listView.paginationChange.emit();
    }
}

export class ListViewField {
    listView: ListView;
    name: string;
    label: string;
    hidden: boolean;
    maxWidth: string;
    headNowrap: boolean;
    headAlign: 'center' | 'right' | 'left' | string;
    headStyle: any;
    headTemplateEnabled: boolean;
    dataTemplateEnabled: boolean;
    formater: Function;
    index: number;
    sort: ListViewSort;
    filter: ListViewFilter;
    query: ListViewQuery;
    dictionary: any;
    dictionaryRef: string;
    contentNowrap: boolean;
    contentAlign: 'center' | 'right' | 'left' | string;
    contentStyle: any;

    constructor(listView: ListView, options?: ListViewFieldOptions | any) {
        this.listView = listView;
        this.patchOptions(options);
    }
    private patchOptions(options?: any) {
        if (!options) { options = {}; }
        this.headNowrap = this.listView.headNowrap;
        this.contentNowrap = this.listView.contentNowrap;
        this.dictionary = options.dictionary;
        ObjectUtils.patchDataWhileNotNull(options, this, true);

        const _self = this;
        this.formater = options.formater || ((v, row) => {
            try {
                if (_self.dictionary) return _self.dictionary[v];
            } catch (e) { } return v;
        });

        this.sort = (options.sort instanceof ListViewSort ? options.sort : new ListViewSort(this, options.sort));
        this.filter = (options.filter instanceof ListViewFilter ? options.filter : new ListViewFilter(this, options.filter));
        this.query = (options.query instanceof ListViewQuery ? options.query : new ListViewQuery(this, options.query));

        this.headStyle = options.headStyle;
        if (typeof (this.headStyle) === 'string') try { this.headStyle = JSON.parse(this.headStyle); } catch (e) { }
        if (!this.headStyle) this.headStyle = {};
        if (this.headAlign != null) this.headStyle['text-align'] = this.headAlign;

        this.contentStyle = options.contentStyle;
        if (typeof (this.contentStyle) === 'string') try { this.contentStyle = JSON.parse(this.contentStyle); } catch (e) { }
        if (!this.contentStyle) this.contentStyle = {};

        if (this.maxWidth != null) this.contentStyle['max-width'] = this.maxWidth;
        if (this.contentAlign != null) {
            this.contentAlign = this.contentAlign.toLowerCase();
            this.contentStyle['text-align'] = this.contentAlign;
            if (this.sort.enabled
                && (this.contentAlign == 'center' || this.contentAlign == 'right')
                && this.contentStyle['padding-right'] == null)
                this.contentStyle['padding-right'] = '24px';
        }
    }
}
export class ListViewSort {
    field: ListViewField;
    enabled: boolean = false;
    value: ListViewSortDirect | string = null; // descend|ascend|null
    sortIndex: number;

    constructor(field: ListViewField, options?: ListViewSortOptions | any) {
        this.field = field;
        this.patchOptions(options);
    }

    private patchOptions(options?: any) {
        if (!options) { options = {}; }
        ObjectUtils.patchDataWhileNotNull(options, this, true);
    }
}
// { enabled: true, value:[], items:[{ text: '优', value: 1, seq: 2, selected:false }, { text: '良', value: 2, seq: 1 }] }
export class ListViewFilter {
    private _value: string[] | number[] | any[];
    items: ListViewFilterItem[];
    field: ListViewField;
    enabled: boolean = false;

    constructor(field: ListViewField, options?: ListViewFilterOptions | any) {
        this.field = field;
        this.patchOptions(options);
    }

    set value(value: string[] | number[] | any[]) {
        this._value = value;
    }
    get value(): string[] | number[] | any[] {
        if (this.items && this.items.length > 0) {
            this._value = this.items.filter(item => item.selected).map(item => item.value);
        }
        return this._value;
    }

    private patchOptions(options?: any) {
        if (!options) { options = {}; }
        ObjectUtils.patchDataWhileNotNull(options, this, true);

        if (options.items) {
            this.items = options.items
                .filter(item => item != null)
                .map(item => {
                    if (item instanceof ListViewFilterItem) {
                        item.filter = this;
                        return item;
                    } else {
                        return new ListViewFilterItem(this, item);
                    }
                });
        } else {
            const dictKind = this.field.dictionaryRef;
            if (this.items == null && dictKind) {
                const listviewInjector = this.field.listView.injector;
                if (listviewInjector != null) this.loadItemsByDictService(listviewInjector, dictKind);
                else this.field.listView.injectorAssigned.subscribe(injector => {
                    this.loadItemsByDictService(injector, dictKind);
                });
            }
        }
    }
    private loadItemsByDictService(injector, dictKind) {
        const dictService = injector.get(DataDictService);
        dictService.getItems(dictKind).subscribe(dictItems => {
            this.items = [];
            if (dictItems) dictItems.sort(sortIndexAscend).forEach(dictItem => {
                this.items.push(new ListViewFilterItem(this, {
                    text: dictService.parseItemText(dictKind, dictItem),
                    value: dictItem.value,
                    seq: dictItem.seq
                }));
            });
        });
    }
}
// { text: '优', value: '1', seq: 1, selected:false }
export class ListViewFilterItem {
    filter: ListViewFilter;
    text: string;
    value: any;
    seq: number;
    selected: boolean;

    constructor(filter: ListViewFilter, options?: ListViewFilterItemOptions | any) {
        this.filter = filter;
        this.patchOptions(options);
    }

    private patchOptions(options?: any) {
        if (!options) { options = {}; }
        ObjectUtils.patchDataWhileNotNull(options, this, true);
    }
}
export class ListViewQuery {
    field: ListViewField;
    value: any;
    constructor(field: ListViewField, options?: any) {
        this.field = field;
        this.patchOptions(options);
    }
    private patchOptions(options?: any) {
        if (!options) { options = {}; }
        ObjectUtils.patchDataWhileNotNull(options, this, true);
    }
}
